/**
 * useSgpBetSlip — bet-slip state + leg management for SgpFlowScreen.
 * Extracted verbatim from SgpFlowScreen.jsx (god-file decomposition).
 * Owns: selected legs, bet mode, stake, share modal, submission flow,
 * AI parlay probabilities, and localStorage draft persistence.
 */

import { useState, useEffect } from 'react';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { db, auth } from '../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

export default function useSgpBetSlip({ userProfile, pendingQuery, navigateTo }) {
    const [selectedLegs, setSelectedLegs] = useState([]); // selected legs
    const [fetchingAiProbs, setFetchingAiProbs] = useState(false);
    const [betMode, setBetMode] = useState(() => {
        if (Platform.OS === 'web') {
            try {
                const stored = window.sessionStorage.getItem('sgp:mode');
                if (stored) return stored;
            } catch {}
        }
        // Use user's default bet mode preference if available
        return userProfile?.preferences?.defaultBetMode || 'infer';
    }); // 'infer' | 'sgp' | 'straight' | 'sgpx'
    const [builderStake, setBuilderStake] = useState(10);
    const [activeFixtureId, setActiveFixtureId] = useState(null);
    const [lastNotice, setLastNotice] = useState('');
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const [showBetSlipModal, setShowBetSlipModal] = useState(false);
    const [showSubmissionScreen, setShowSubmissionScreen] = useState(false);

    // Add a fetched leg (from AI results) to the local bet slip
    const computeCap = () => {
        return 5; // All users can select up to 5 legs
    };

    const handleAddFetchedLeg = (legOption) => {
        if (!legOption) return;
        const autoSwitch = userProfile?.preferences?.autoSwitchMode !== false; // Default to true
        
        setSelectedLegs((prev) => {
            const exists = prev.find((l) => l.id === legOption.id);
            if (exists) {
                // deselect
                const next = prev.filter((l) => l.id !== legOption.id);
                setLastNotice(''); // Clear notice when deselecting
                return next;
            }
            // Role-based cap
            const cap = computeCap();
            if (prev.length >= cap) { 
                setLastNotice(`Selection limit reached (${cap}).`); 
                return prev; 
            }
            
            const fixId = legOption?.raw?.fixture_id || legOption?.raw?.game_id || null;
            const newLegs = [...prev, legOption];
            
            // Auto-switching logic
            if (betMode === 'infer') {
                // Infer mode: automatically determine bet type based on selections
                const fixtures = newLegs.map(l => l?.raw?.fixture_id || l?.raw?.game_id).filter(Boolean);
                const uniqueFixtures = [...new Set(fixtures)];
                
                if (uniqueFixtures.length === 1 && newLegs.length >= 2) {
                    // All legs from same game -> SGP
                    setLastNotice('ℹ️ Multiple legs from same game - displaying as SGP');
                    if (!activeFixtureId) setActiveFixtureId(uniqueFixtures[0]);
                } else if (uniqueFixtures.length > 1) {
                    // Legs from different games -> could be Straight or SGPx
                    setLastNotice('ℹ️ Legs from different games - displaying as separate bets');
                } else {
                    setLastNotice('');
                }
            } else if (betMode === 'sgp') {
                // SGP mode: enforce same-game restriction
                if (!activeFixtureId && fixId) {
                    setActiveFixtureId(fixId);
                } else if (activeFixtureId && fixId && activeFixtureId !== fixId) {
                    // Incompatible: either switch modes or block selection
                    if (autoSwitch) {
                        setBetMode('sgpx');
                        if (Platform.OS === 'web') { try { window.sessionStorage.setItem('sgp:mode', 'sgpx'); } catch {} }
                        setLastNotice('⚠️ Switched to SGPx mode: legs are from different games. You can switch to "Straight" if you prefer separate bets.');
                    } else {
                        setLastNotice('❌ Cannot add leg from different game in SGP mode. Switch to SGPx or Straight mode, or enable Auto-Switch in settings.');
                        return prev; // Block the selection
                    }
                }
            } else if (betMode === 'straight') {
                // Straight mode: each leg is independent
                if (!activeFixtureId && fixId) setActiveFixtureId(fixId);
                
                // Check if all legs are now from same game
                const fixtures = newLegs.map(l => l?.raw?.fixture_id || l?.raw?.game_id).filter(Boolean);
                const uniqueFixtures = [...new Set(fixtures)];
                if (uniqueFixtures.length === 1 && newLegs.length >= 2) {
                    setLastNotice('💡 Tip: All your legs are from the same game. Consider switching to "SGP" mode for parlay odds.');
                }
            } else if (betMode === 'sgpx') {
                // SGPx mode: allow mixed bets with SGP + straight legs
                // No restrictions, just track fixtures
                if (!activeFixtureId && fixId) setActiveFixtureId(fixId);
                setLastNotice('');
            }
            
            return newLegs;
        });
    };

    // Fetch real market average probabilities from OpticOdds parlay endpoint
    const fetchAiProbabilities = async (legs) => {
        if (!legs || legs.length < 2) return; // Need at least 2 legs for parlay
        
        setFetchingAiProbs(true);
        try {
            const apiUrl = Constants.expoConfig?.extra?.apiUrl || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
            
            // Build parlay entries from selected legs
            const entries = legs.map(leg => {
                const raw = leg?.raw || {};
                const price = raw.price ?? raw.american_price ?? raw.odds ?? -110;
                return {
                    market: raw.market || 'Unknown',
                    name: raw.name || raw.selection || leg.title || 'Unknown',
                    fixture_id: raw.fixture_id || null,
                    price_american: price
                };
            }).filter(e => e.fixture_id); // Only include legs with fixture_id
            
            if (entries.length < 2) {
                console.log('Not enough legs with fixture_id for AI parlay pricing');
                return;
            }
            
            // Call OpticOdds parlay endpoint via our proxy
            const response = await fetch(`${apiUrl}/opticOddsProxy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    path: '/parlay/odds',
                    method: 'POST',
                    body: {
                        sportsbooks: ['OpticOdds AI'],
                        entries
                    }
                })
            });
            
            if (!response.ok) {
                console.warn('AI prob fetch failed:', response.status);
                return;
            }
            
            const data = await response.json();
            console.log('AI parlay response:', data);
            
            // Update selected legs with market average probabilities if available
            // OpticOdds returns fair odds and probabilities for the entire parlay
            // For individual legs, we can derive from the response or keep our estimates
            if (data?.data?.legs) {
                setSelectedLegs(prev => prev.map((leg, idx) => {
                    const aiLeg = data.data.legs[idx];
                    if (aiLeg?.probability) {
                        return {
                            ...leg,
                            ai_prob: aiLeg.probability,
                            ai_prob_source: 'OpticOdds AI'
                        };
                    }
                    return leg;
                }));
            }
        } catch (err) {
            console.error('Error fetching market average probabilities:', err);
        } finally {
            setFetchingAiProbs(false);
        }
    };

    // Fetch market average probabilities when legs are selected (debounced)
    useEffect(() => {
        if (selectedLegs.length < 2) return;
        
        const timer = setTimeout(() => {
            fetchAiProbabilities(selectedLegs);
        }, 1000); // Wait 1s after selection changes
        
        return () => clearTimeout(timer);
    }, [selectedLegs.length]); // Only trigger on count change to avoid loops

    // Save current selected legs to Firestore under users/{uid}/betslips
    const handleSaveBetSlip = async () => {
        try {
            const user = auth?.currentUser;
            if (!user || selectedLegs.length === 0) return;
            
            const legs = selectedLegs.map(l => ({
                id: l.id,
                market: l.raw?.market || l.raw?.market_id || l.title || '',
                selection: l.raw?.selection || l.raw?.name || l.description || '',
                price: l.raw?.price ?? l.raw?.american_price ?? l.raw?.odds ?? null,
                sportsbook: l.raw?.sportsbook || l.raw?.book || '',
                deep_link: l.raw?.urls?.offer || l.raw?.urls?.deeplink || l.raw?.urls?.event || l.raw?.urls?.market || l.raw?.deep_link?.desktop || null,
                // Game information
                home_team: l.raw?.home_team_display || l.raw?.home_team || '',
                away_team: l.raw?.away_team_display || l.raw?.away_team || '',
                is_live: l.raw?.is_live || false,
                status: l.raw?.status || 'unplayed',
                // Logos
                sportsbook_logo: l.raw?.sportsbook_logo || l.raw?.book_logo || null,
                home_logo: l.raw?.home_logo || l.raw?.home_team_logo || null,
                away_logo: l.raw?.away_logo || l.raw?.away_team_logo || null,
                team_logo: l.raw?.team_logo || null,
            }));
            
            await addDoc(collection(db, 'users', user.uid, 'betslips'), {
                createdAt: serverTimestamp(),
                legs,
                query: pendingQuery || ''
            });
            
            setSelectedLegs([]);
        } catch (e) {
            console.error('[handleSaveBetSlip] Error:', e);
        }
    };

    // Handle bet slip submission
    const handleBetSlipSubmission = () => {
        setShowBetSlipModal(false);
        setShowSubmissionScreen(true);
    };

    // Persist and restore draft to local storage
    useEffect(() => {
        if (Platform.OS === 'web') {
            try {
                const raw = window.localStorage.getItem('sgp:draft');
                if (raw) {
                    const arr = JSON.parse(raw);
                    if (Array.isArray(arr)) setSelectedLegs(arr);
                }
            } catch {}
        }
    }, []);
    useEffect(() => {
        if (Platform.OS === 'web') {
            try { window.localStorage.setItem('sgp:draft', JSON.stringify(selectedLegs)); } catch {}
        }
    }, [selectedLegs]);

    // Handle submission completion
    const handleSubmissionComplete = () => {
        setShowSubmissionScreen(false);
        try { if (Platform.OS === 'web') window.localStorage.removeItem('sgp:draft'); } catch {}
        navigateTo('Home');
    };

    // Handle sharing betslip
    const handleShareBetSlip = () => {
        if (selectedLegs.length === 0) return;
        
        // Create a shareable betslip object
        const betslipData = {
            legs: selectedLegs.map(leg => ({
                id: leg.id,
                title: leg.title,
                description: leg.description,
                raw: {
                    price: leg.raw?.price,
                    american_price: leg.raw?.american_price,
                    market: leg.raw?.market,
                    selection: leg.raw?.selection,
                    sportsbook: leg.raw?.sportsbook,
                    fixture_id: leg.raw?.fixture_id,
                    ai_prob: leg.raw?.ai_prob
                }
            })),
            betMode,
            stake: builderStake,
            timestamp: new Date().toISOString()
        };
        
        // Encode the betslip data as base64
        const encodedData = btoa(JSON.stringify(betslipData));
        const shareUrl = `${window.location.origin}/#/sgp?betslip=${encodedData}`;
        
        setShareUrl(shareUrl);
        setShowShareModal(true);
    };

    // Handle copying share URL
    const handleCopyShareUrl = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            // You could show a toast notification here
            console.log('Share URL copied to clipboard');
        } catch (error) {
            console.error('Failed to copy URL:', error);
        }
    };

    return {
        selectedLegs, setSelectedLegs,
        fetchingAiProbs, setFetchingAiProbs,
        betMode, setBetMode,
        builderStake, setBuilderStake,
        activeFixtureId, setActiveFixtureId,
        lastNotice, setLastNotice,
        showShareModal, setShowShareModal,
        shareUrl, setShareUrl,
        showBetSlipModal, setShowBetSlipModal,
        showSubmissionScreen, setShowSubmissionScreen,
        computeCap,
        handleAddFetchedLeg,
        fetchAiProbabilities,
        handleSaveBetSlip,
        handleBetSlipSubmission,
        handleSubmissionComplete,
        handleShareBetSlip,
        handleCopyShareUrl,
    };
}
